import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { regionDigitsKey, saveParameter } from './suggestionParametersApi';

/**
 * „Dodaj kraj” w sekcji „Region na pulpitach FTL” (21.10.2026, S-21.10-25,
 * `regionDigitsByCountry`): kraj ISO, liczba cyfr kodu pocztowego 0–2
 * i powód — zapis tą samą trasą co każdy parametr, z historią. Sprawdzenie
 * tutaj to tylko podpowiedź przy literówce; rozstrzyga API.
 */

/** Wpis z formularza → kraj i cyfry albo komunikat. */
export function parseRegionCountry(
  country: string,
  digits: string,
  existing: readonly string[],
): { country: string; digits: number } | { error: string } {
  const c = country.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) {
    return { error: 'Kraj: dwuliterowy kod ISO, np. AT' };
  }
  if (existing.includes(regionDigitsKey(c))) {
    return { error: `Kraj ${c} jest już na liście — zmień jego wartość w tabeli` };
  }
  const n = Number(digits.trim());
  if (digits.trim() === '' || !Number.isInteger(n) || n < 0 || n > 2) {
    return { error: 'Cyfry kodu: 0, 1 albo 2 (0 = sam kraj)' };
  }
  return { country: c, digits: n };
}

export function AddRegionCountryDialog({
  existing,
  onClose,
  onSaved,
}: {
  existing: readonly string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [country, setCountry] = useState('');
  const [digits, setDigits] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const parsed = parseRegionCountry(country, digits, existing);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    if (reason.trim() === '') {
      setError('Podaj powód zmiany');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveParameter(regionDigitsKey(parsed.country), parsed.digits, reason.trim());
      onSaved();
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Region na pulpitach FTL — nowy kraj</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ile pierwszych cyfr kodu pocztowego bierze region w tym kraju (0 = sam kraj). Zmiana trafia do historii.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Kraj (kod ISO)"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { maxLength: 2 } }}
          />
          <TextField
            label="Cyfry kodu, zakres 0–2"
            value={digits}
            onChange={(e) => setDigits(e.target.value)}
            size="small"
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          />
          <TextField
            label="Powód zmiany"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </Stack>
        {error && (
          <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button onClick={save} variant="contained" disabled={busy}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
}
